// Phase 6.AN — Unified startAnalyze entry point
// ---------------------------------------------------------------------------
// EVERY navigation to /analyze/:ticker — homepage search submit, suggestion
// click, navbar search, error-page search, popular-ticker chip, landing CTA,
// pricing CTA, watchlist empty state, programmatic redirect — MUST go
// through this function.
//
// Why? Before this existed, each entry point had its own `navigate()` call,
// each had a different approach to stashing provenance, and the Analyze
// component relied on a `useEffect([tk])` re-run to clear stale React
// state. That created a real bug class: when the user analyzed AAPL,
// got a timeout, then typed MU, the AAPL `notFoundReason="timeout"`
// could survive because:
//   1. The Analyze component didn't re-mount (React Router reuses it)
//   2. The state reset useEffect raced with the render that read
//      `notFoundReason`
//   3. If the auth gate bailed (session quietly expired), state never
//      got cleared at all
//
// `startAnalyze` does all three at the source, in one place, every time:
//   * normalize the ticker
//   * stash provenance for the next Analyze mount to pick up
//   * emit the `home_search_submitted` / `cta_navigated` lifecycle event
//   * actually `navigate(...)` so React Router transitions
//   * The Analyze route is wrapped in `<AnalyzeRouteWrapper>` which
//     keys the actual `<Analyze>` by ticker so a NEW Analyze tree is
//     mounted on every ticker change (impossible for stale state to
//     survive).

import { stashTickerProvenance } from "@/lib/ticker_provenance";
import { emitLifecycle, newClientTraceId } from "@/lib/lifecycle";

/**
 * Normalize whatever the user typed/clicked into a canonical ticker
 * symbol suitable for /analyze/:ticker URLs.
 *
 * @param {string} input — typed query OR pre-resolved ticker symbol
 * @returns {string} uppercased, trimmed, stripped to safe URL chars,
 *                   never empty (defaults to ""); caller decides bail.
 */
export function normalizeTicker(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  // Keep letters, digits, dot, dash. Strip anything else (whitespace,
  // emojis, smart quotes, etc.) that could break the route param.
  return raw.toUpperCase().replace(/[^A-Z0-9.\-]/g, "");
}

/**
 * Start a new analyze attempt.
 *
 * @param {object} args
 * @param {function} args.navigate — react-router navigate() from useNavigate()
 * @param {string}   args.ticker   — pre-resolved ticker symbol (preferred)
 *                                    OR raw user input (will be normalized
 *                                    but caller should resolve company-name
 *                                    → ticker before calling when possible)
 * @param {string} [args.typedRaw] — what the user actually typed/clicked
 *                                    (defaults to args.ticker)
 * @param {string} [args.source]   — provenance source tag, e.g.
 *                                    "stock_search:suggestion",
 *                                    "stock_search:enter_typed",
 *                                    "landing:cta_hero_analyze_first",
 *                                    "watchlist:empty_cta",
 *                                    "stock_not_found:suggestion",
 *                                    "pricing:free_cta".
 * @returns {string} the resolved ticker (empty string if normalization
 *                   produced nothing; caller should noop in that case).
 */
export function startAnalyze({ navigate, ticker, typedRaw, source = "unknown" }) {
  const target = normalizeTicker(ticker);
  if (!target) return "";
  const typed = (typedRaw !== undefined && typedRaw !== null) ? String(typedRaw) : String(ticker || "");

  // 1) Stash provenance so the next Analyze mount can read it and
  //    include `typed_ticker` in its lifecycle telemetry.
  try {
    stashTickerProvenance({ typed, target, source });
  } catch { /* swallow */ }

  // 2) Emit a unified `analyze_started_by_user` lifecycle event covering
  //    EVERY entry path. The Owner Console Frontend Navigation / Error
  //    Trace will show this as the first row of every user-initiated
  //    analyze attempt, so the operator can definitively prove
  //    "the user did click the analyze CTA for MU at 14:32".
  try {
    const traceId = newClientTraceId();
    emitLifecycle("analyze_started_by_user", {
      ticker: target,
      client_trace_id: traceId,
      extra: {
        typed_raw: typed,
        target,
        source,
        normalized_from_company_name: typed.toUpperCase().replace(/[^A-Z0-9.\-]/g, "") !== target,
        destination_path: `/analyze/${target}`,
        pathname: (typeof window !== "undefined") ? window.location.pathname : null,
      },
    });
  } catch { /* swallow */ }

  // 3) Actually navigate. React Router transitions the URL; the
  //    AnalyzeRouteWrapper component will then mount a FRESH <Analyze>
  //    subtree keyed by `target`, guaranteeing all stale state from
  //    previous attempts is discarded.
  try {
    if (typeof navigate === "function") {
      navigate(`/analyze/${target}`);
    } else if (typeof window !== "undefined") {
      // Defensive fallback if a caller forgot to pass navigate.
      window.location.assign(`/analyze/${target}`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[startAnalyze] navigation failed", err);
  }
  return target;
}
